/**
 * Structured form model for a carrier's mapping config — the round-trip between a
 * stored/inferred config JSON and an editable, form-friendly draft. Lets the admin
 * console edit every field of a carrier config WITHOUT hand-writing JSON.
 *
 * Pure + unit-tested. `buildCarrierConfig(draft)` produces the config the API
 * validates (packages/config carrierConfigSchema); `draftFromConfig(cfg)` is the
 * inverse. Round-trip is lossless for every field the mapping engine reads.
 *
 * NOTE ON TRANSFORMS: the mapping engine resolves transforms by TARGET (the
 * canonical field), not the source header — `config.transforms[target]`
 * (packages/core mapping.ts). Legacy hand-authored configs that keyed transforms
 * by source header were never applied (the engine fell back to defaults); this
 * model keys them correctly by target, so they finally take effect — and drops the
 * dead source-keyed entries, which is a no-op for ingest behavior.
 */

export type TransformType =
  | ''
  | 'currency'
  | 'number'
  | 'integer'
  | 'percent'
  | 'date'
  | 'excel-date'
  | 'string';

export interface TransformDraft {
  type: TransformType; // '' = none (engine applies a sensible default)
  stripChars: string; // currency / number / integer
  asFraction: boolean; // percent
  format: string; // date
  trim: boolean; // string
  uppercase: boolean; // string
  lowercase: boolean; // string
}

export interface MappingRowDraft {
  id: string;
  source: string; // raw header in the file
  target: string; // canonical column, 'extra.<name>', or '' (ignore → kept in raw)
  transform: TransformDraft;
}

export interface SkipRuleDraft {
  id: string;
  column: string;
  pattern: string;
}

export interface CarrierConfigDraft {
  carrierSlug: string;
  version: string;
  fileType: 'csv' | 'xlsx';
  delimiter: string;
  encoding: string;
  sheetName: string;
  headerRow: string;
  idNormalization: 'default' | 'numeric';
  productLine: 'major_medical' | 'medicare' | 'ancillary';
  memberKey: string; // naturalKeys.memberExternalId (a source header)
  policyKey: string; // naturalKeys.policyNumber (optional source header)
  mappings: MappingRowDraft[];
  /** Advanced: positional column names overriding the file's header row. */
  columns: string; // one per line
  /** Advanced: skip a row when any of these columns is empty. */
  requireColumns: string; // one per line
  /** Advanced: skip a row when a column matches a regex. */
  skipRows: SkipRuleDraft[];
  /** Advanced: fields a real data row must have (default member id + commission). */
  validationRequired: string; // one per line
  privacyDrop: string[];
  privacyHash: string[];
  keepRawRow: boolean;
}

let _seq = 0;
const rid = () => `cfgrow-${_seq++}`;

const emptyTransform = (): TransformDraft => ({
  type: '',
  stripChars: '',
  asFraction: false,
  format: '',
  trim: false,
  uppercase: false,
  lowercase: false,
});

export function emptyCarrierConfigDraft(): CarrierConfigDraft {
  return {
    carrierSlug: '',
    version: '1',
    fileType: 'csv',
    delimiter: ',',
    encoding: 'utf-8',
    sheetName: '',
    headerRow: '1',
    idNormalization: 'default',
    productLine: 'major_medical',
    memberKey: '',
    policyKey: '',
    mappings: [],
    columns: '',
    requireColumns: '',
    skipRows: [],
    validationRequired: '',
    privacyDrop: [],
    privacyHash: [],
    keepRawRow: true,
  };
}

export const mappingRowDraft = (p: Partial<MappingRowDraft> = {}): MappingRowDraft => ({
  id: rid(),
  source: '',
  target: '',
  transform: emptyTransform(),
  ...p,
});

export const skipRuleDraft = (p: Partial<SkipRuleDraft> = {}): SkipRuleDraft => ({
  id: rid(),
  column: '',
  pattern: '',
  ...p,
});

const lines = (s: string): string[] =>
  s
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter(Boolean);

const fromLines = (xs: unknown): string => (Array.isArray(xs) ? xs.map(String).join('\n') : '');

/** A transform draft → the config object the schema validates (or null = omit). */
function transformToConfig(t: TransformDraft): Record<string, unknown> | null {
  switch (t.type) {
    case '':
      return null;
    case 'currency':
    case 'number':
    case 'integer':
      return { type: t.type, ...(t.stripChars ? { stripChars: t.stripChars } : {}) };
    case 'percent':
      return { type: 'percent', ...(t.asFraction ? { asFraction: true } : {}) };
    case 'date':
      return { type: 'date', ...(t.format ? { format: t.format } : {}) };
    case 'excel-date':
      return { type: 'excel-date' };
    case 'string':
      return {
        type: 'string',
        ...(t.trim ? { trim: true } : {}),
        ...(t.uppercase ? { uppercase: true } : {}),
        ...(t.lowercase ? { lowercase: true } : {}),
      };
  }
}

function transformFromConfig(raw: unknown): TransformDraft {
  const t = emptyTransform();
  if (!raw || typeof raw !== 'object') return t;
  const o = raw as Record<string, unknown>;
  const type = o.type;
  if (
    type === 'currency' ||
    type === 'number' ||
    type === 'integer' ||
    type === 'percent' ||
    type === 'date' ||
    type === 'excel-date' ||
    type === 'string'
  ) {
    t.type = type;
  }
  if (typeof o.stripChars === 'string') t.stripChars = o.stripChars;
  if (o.asFraction === true) t.asFraction = true;
  if (typeof o.format === 'string') t.format = o.format;
  if (o.trim === true) t.trim = true;
  if (o.uppercase === true) t.uppercase = true;
  if (o.lowercase === true) t.lowercase = true;
  return t;
}

/** Build the config JSON the API validates. Optional fields are omitted unless set. */
export function buildCarrierConfig(d: CarrierConfigDraft): Record<string, unknown> {
  const columnMapping: Record<string, string> = {};
  const transforms: Record<string, unknown> = {};
  for (const row of d.mappings) {
    const source = row.source.trim();
    const target = row.target.trim();
    if (!source || !target) continue;
    columnMapping[source] = target;
    const t = transformToConfig(row.transform);
    if (t) transforms[target] = t;
  }

  const cfg: Record<string, unknown> = {
    carrierSlug: d.carrierSlug.trim(),
    version: Number(d.version) || 1,
    fileType: d.fileType,
    headerRow: Number(d.headerRow) || 1,
    productLine: d.productLine,
    naturalKeys: {
      memberExternalId: d.memberKey.trim(),
      ...(d.policyKey.trim() ? { policyNumber: d.policyKey.trim() } : {}),
    },
    columnMapping,
  };

  if (d.fileType === 'csv' && d.delimiter && d.delimiter !== ',') cfg.delimiter = d.delimiter;
  if (d.encoding && d.encoding !== 'utf-8') cfg.encoding = d.encoding;
  if (d.fileType === 'xlsx' && d.sheetName.trim()) cfg.sheetName = d.sheetName.trim();
  if (d.idNormalization !== 'default') cfg.idNormalization = d.idNormalization;
  if (Object.keys(transforms).length > 0) cfg.transforms = transforms;

  const cols = lines(d.columns);
  if (cols.length > 0) cfg.columns = cols;
  const req = lines(d.requireColumns);
  if (req.length > 0) cfg.requireColumns = req;
  const skip = d.skipRows
    .filter((s) => s.column.trim() && s.pattern.trim())
    .map((s) => ({ column: s.column.trim(), pattern: s.pattern.trim() }));
  if (skip.length > 0) cfg.skipRowsWhere = skip;
  const valReq = lines(d.validationRequired);
  if (valReq.length > 0) cfg.validation = { required: valReq };

  if (d.privacyDrop.length > 0 || d.privacyHash.length > 0 || !d.keepRawRow) {
    cfg.privacy = { drop: d.privacyDrop, hash: d.privacyHash, keepRawRow: d.keepRawRow };
  }
  return cfg;
}

/** Parse a stored/inferred config object into an editable draft. */
export function draftFromConfig(input: unknown): CarrierConfigDraft {
  const d = emptyCarrierConfigDraft();
  if (!input || typeof input !== 'object') return d;
  const c = input as Record<string, unknown>;

  if (typeof c.carrierSlug === 'string') d.carrierSlug = c.carrierSlug;
  if (c.version != null) d.version = String(c.version);
  if (c.fileType === 'csv' || c.fileType === 'xlsx') d.fileType = c.fileType;
  if (typeof c.delimiter === 'string') d.delimiter = c.delimiter;
  if (typeof c.encoding === 'string') d.encoding = c.encoding;
  if (typeof c.sheetName === 'string') d.sheetName = c.sheetName;
  if (c.headerRow != null) d.headerRow = String(c.headerRow);
  if (c.idNormalization === 'numeric') d.idNormalization = 'numeric';
  if (
    c.productLine === 'medicare' ||
    c.productLine === 'ancillary' ||
    c.productLine === 'major_medical'
  )
    d.productLine = c.productLine;

  const nk = (c.naturalKeys ?? {}) as Record<string, unknown>;
  if (typeof nk.memberExternalId === 'string') d.memberKey = nk.memberExternalId;
  if (typeof nk.policyNumber === 'string') d.policyKey = nk.policyNumber;

  const mapping = (c.columnMapping ?? {}) as Record<string, string>;
  const transforms = (c.transforms ?? {}) as Record<string, unknown>;
  d.mappings = Object.entries(mapping).map(([source, target]) =>
    mappingRowDraft({ source, target, transform: transformFromConfig(transforms[target]) }),
  );

  d.columns = fromLines(c.columns);
  d.requireColumns = fromLines(c.requireColumns);
  if (Array.isArray(c.skipRowsWhere)) {
    d.skipRows = (c.skipRowsWhere as Record<string, unknown>[]).map((s) =>
      skipRuleDraft({ column: String(s.column ?? ''), pattern: String(s.pattern ?? '') }),
    );
  }
  const val = (c.validation ?? {}) as Record<string, unknown>;
  d.validationRequired = fromLines(val.required);

  const priv = (c.privacy ?? {}) as { drop?: unknown; hash?: unknown; keepRawRow?: unknown };
  if (Array.isArray(priv.drop)) d.privacyDrop = priv.drop.map(String);
  if (Array.isArray(priv.hash)) d.privacyHash = priv.hash.map(String);
  if (priv.keepRawRow === false) d.keepRawRow = false;
  return d;
}
